// src/pages/blog/SmartDcaVsDcaCaseStudy.jsx
import { Head } from "@unhead/react";
import { Link } from "react-router-dom";
import marketingChart from "./Marketing Chart.png";

export default function SmartDcaVsDcaCaseStudy() {
  const title =
    "Smart DCA vs Regular DCA: 3-Month Bitcoin Case Study";

  const description =
    "Compare Smart DCA vs regular Bitcoin DCA in a 3-month historical case study using the same budget and bi-weekly cadence. See the methodology, results, and limitations.";

  const canonical =
    "https://satstacker.app/blog/smart-dca-vs-regular-dca";

  const published = "January 2026";
  const updated = "September 2026";

  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: title,
    description,
    datePublished: "2026-01-16",
    dateModified: "2026-09-22",
    author: {
      "@type": "Organization",
      name: "SatStacker",
      url: "https://satstacker.app",
    },
    publisher: {
      "@type": "Organization",
      name: "SatStacker",
      url: "https://satstacker.app",
    },
    image: [
      "https://satstacker.app/static/blog-preview.jpg",
    ],
    mainEntityOfPage: canonical,
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "SatStacker",
        item: "https://satstacker.app/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: "https://satstacker.app/blog",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Smart DCA vs Regular DCA",
        item: canonical,
      },
    ],
  };

  const faqItems = [
    {
      q: "What is the difference between Smart DCA and regular DCA?",
      a: "Regular DCA buys a planned amount on a fixed schedule. Smart DCA keeps the recurring budget and cadence but adds rules around when the planned purchase executes inside each buying window.",
    },
    {
      q: "Did Smart Timing beat regular DCA in this case study?",
      a: "Yes. In the three-month historical period shown here, SatStacker's Smart Timing accumulated more Bitcoin than the comparison strategy that bought at the scheduled DCA time, using the same planned budget.",
    },
    {
      q: "Does this prove Smart DCA will always outperform regular DCA?",
      a: "No. This is one historical period. Future price paths can be different, and Smart Timing cannot guarantee a better execution price in every buying window.",
    },
    {
      q: "Did the Smart DCA comparison invest more money?",
      a: "No. The comparison is intended to use the same planned budget and the same bi-weekly cadence. The difference is execution timing inside the buying windows.",
    },
    {
      q: "Can I test a different Bitcoin DCA schedule?",
      a: "Yes. SatStacker's Bitcoin DCA calculator lets you choose a daily, weekly, or bi-weekly cadence and compare historical Smart Timing results with scheduled DCA.",
    },
  ];

  return (
    <main className="min-h-screen bg-zinc-950 text-white">
      <article className="max-w-3xl mx-auto px-5 py-12">
        <Head>
          <title>{title} | SatStacker</title>

          <meta
            name="description"
            content={description}
          />

          <link
            rel="canonical"
            href={canonical}
          />

          <meta property="og:type" content="article" />
          <meta property="og:title" content={title} />
          <meta
            property="og:description"
            content={description}
          />
          <meta property="og:url" content={canonical} />
          <meta
            property="og:site_name"
            content="SatStacker"
          />
          <meta
            property="og:image"
            content="https://satstacker.app/static/blog-preview.jpg"
          />

          <meta
            name="twitter:card"
            content="summary_large_image"
          />
          <meta
            name="twitter:title"
            content={title}
          />
          <meta
            name="twitter:description"
            content={description}
          />
          <meta
            name="twitter:image"
            content="https://satstacker.app/static/blog-preview.jpg"
          />

          <script type="application/ld+json">
            {JSON.stringify(schema)}
          </script>

          <script type="application/ld+json">
            {JSON.stringify(breadcrumbSchema)}
          </script>
        </Head>

        <Link
          to="/blog"
          className="text-sm text-zinc-500 hover:text-orange-400"
        >
          ← SatStacker Blog
        </Link>

        <header className="mt-8 mb-10">
          <p className="text-xs uppercase tracking-[0.18em] text-orange-400 mb-4">
            Historical Bitcoin DCA Case Study
          </p>

          <h1 className="text-3xl md:text-4xl font-bold leading-tight mb-4">
            Smart DCA vs Regular DCA:
            <span className="block text-orange-400">
              A 3-Month Bitcoin Case Study
            </span>
          </h1>

          <p className="text-sm text-zinc-500 mb-6">
            Published {published} · Updated {updated}
          </p>

          <p className="text-lg text-zinc-300 leading-8">
            Does changing <em>when</em> a recurring Bitcoin purchase
            executes make a measurable difference? This case study compares
            standard bi-weekly DCA with SatStacker's Smart Timing over one
            three-month historical period using the same planned budget and
            cadence.
          </p>
        </header>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">
            What this case study compares
          </h2>

          <p className="text-zinc-400 leading-7 mb-5">
            The comparison is deliberately narrow. Both strategies start
            with the same recurring Bitcoin plan. The variable being tested
            is execution timing inside each two-week buying window.
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-5">
              <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">
                Regular DCA baseline
              </p>

              <h3 className="font-semibold text-white mb-2">
                Buy at the scheduled DCA time
              </h3>

              <p className="text-sm text-zinc-400 leading-6 m-0">
                The planned amount is executed at the end of each
                bi-weekly buying window.
              </p>
            </div>

            <div className="rounded-2xl border border-orange-500/20 bg-orange-500/5 p-5">
              <p className="text-xs uppercase tracking-widest text-orange-400 mb-2">
                Smart Timing
              </p>

              <h3 className="font-semibold text-white mb-2">
                Use timing rules inside the same window
              </h3>

              <p className="text-sm text-zinc-400 leading-6 m-0">
                The same planned budget is available, but execution can
                occur later inside the window according to Smart Timing's
                rules.
              </p>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">
            Methodology
          </h2>

          <p className="text-zinc-400 leading-7 mb-5">
            To make the comparison useful, the two strategies need to be
            measured on the same basis.
          </p>

          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5">
            <ul className="space-y-3 text-zinc-300 m-0 pl-5 list-disc">
              <li>
                <strong>Same planned budget</strong> for both strategies
              </li>
              <li>
                <strong>Same bi-weekly cadence</strong>
              </li>
              <li>
                <strong>Same historical market period</strong>
              </li>
              <li>
                <strong>No extra capital</strong> added to Smart Timing
              </li>
              <li>
                <strong>No leverage or trading</strong>
              </li>
              <li>
                Comparison focuses on the Bitcoin accumulated from the
                recurring purchases
              </li>
            </ul>
          </div>

          <p className="text-zinc-500 text-sm leading-6 mt-5">
            This is a historical simulation, not a prediction. Results can
            vary materially with the dates, cadence, execution rules, and
            Bitcoin price path used.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">
            What is regular Bitcoin DCA?
          </h2>

          <p className="text-zinc-400 leading-7 mb-4">
            Dollar-cost averaging means buying a fixed dollar amount of
            Bitcoin on a recurring schedule. A simple implementation executes
            the planned purchase at the same point in every cycle regardless
            of short-term price movement.
          </p>

          <p className="text-zinc-400 leading-7 mb-4">
            The main benefit is consistency: the buying decision is made in
            advance instead of being reconsidered every time Bitcoin moves.
          </p>

          <Link
            to="/bitcoin-dca"
            className="text-orange-400 hover:underline"
          >
            Read the complete Bitcoin DCA guide →
          </Link>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">
            What is Smart DCA?
          </h2>

          <p className="text-zinc-400 leading-7 mb-4">
            Smart DCA keeps the recurring budget and cadence of standard DCA
            but adds rules for when a planned purchase executes inside each
            buying window.
          </p>

          <p className="text-zinc-400 leading-7 mb-4">
            SatStacker's implementation is called Smart Timing. Instead of
            treating the closing price of a window as the only possible entry,
            the strategy can observe price movement inside that window before
            completing the planned purchase.
          </p>

          <Link
            to="/smart-dca"
            className="text-orange-400 hover:underline"
          >
            Learn how Smart DCA works →
          </Link>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">
            The result: Smart DCA vs regular DCA over three months
          </h2>

          <div className="my-7 text-center rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4">
            <img
              src={marketingChart}
              alt="Three-month historical comparison of regular bi-weekly Bitcoin DCA and SatStacker Smart Timing"
              className="rounded-xl mx-auto w-full"
            />

            <p className="text-sm text-zinc-500 mt-3 mb-0">
              Historical comparison using the same planned budget and
              bi-weekly cadence. Chart markers show scheduled DCA and Smart
              Timing executions.
            </p>
          </div>

          <p className="text-zinc-400 leading-7 mb-4">
            In this three-month period, Smart Timing accumulated more Bitcoin
            than the baseline strategy that bought when each bi-weekly window
            closed, while using the same planned recurring budget.
          </p>

          <p className="text-zinc-400 leading-7 mb-4">
            The important point is not that every Smart Timing execution was
            better. A rules-based timing strategy can have windows where the
            baseline entry is better. The result reflects the combined effect
            of the executions across this specific historical period.
          </p>

          <Link
            to="/smart-timing-results"
            className="text-orange-400 hover:underline font-semibold"
          >
            See live Smart Timing results across completed windows →
          </Link>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">
            What this result does — and does not — show
          </h2>

          <div className="space-y-4">
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
              <h3 className="font-semibold text-white mb-2">
                It does show
              </h3>

              <p className="text-zinc-400 leading-7 m-0">
                That changing execution timing inside the same recurring
                Bitcoin plan produced a different accumulation result during
                this historical period.
              </p>
            </div>

            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
              <h3 className="font-semibold text-white mb-2">
                It does not show
              </h3>

              <p className="text-zinc-400 leading-7 m-0">
                That Smart Timing will outperform regular DCA in every period,
                every cadence, or every future market environment.
              </p>
            </div>

            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
              <h3 className="font-semibold text-white mb-2">
                It also does not compare
              </h3>

              <p className="text-zinc-400 leading-7 m-0">
                Smart Timing with investing extra money during dips, active
                trading, leverage, or changing the total planned budget.
              </p>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">
            Why results can change from one period to another
          </h2>

          <p className="text-zinc-400 leading-7 mb-4">
            Smart Timing depends on the price movement that occurs inside each
            buying window. Some windows may contain meaningful pullbacks;
            others may rise steadily or remain relatively flat.
          </p>

          <p className="text-zinc-400 leading-7 mb-4">
            If a lower price becomes available after the window opens, a
            flexible execution strategy may have an opportunity to use it.
            If Bitcoin rises continuously after the window opens, waiting can
            instead produce a less favorable entry.
          </p>

          <p className="text-zinc-400 leading-7">
            That is why a single case study should be treated as evidence
            about one period, not as proof of a universal performance edge.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">
            What about different DCA schedules?
          </h2>

          <p className="text-zinc-400 leading-7 mb-4">
            This case study focuses on a bi-weekly cadence. Daily and weekly
            plans create different buying windows, so they can produce
            different historical results.
          </p>

          <p className="text-zinc-400 leading-7 mb-5">
            SatStacker's calculator lets you test daily, weekly, and
            bi-weekly schedules using your own recurring buy amount.
          </p>

          <Link
            to="/calculator"
            className="inline-block rounded-xl bg-orange-500 px-5 py-3 font-bold text-white hover:bg-orange-600"
          >
            Try the Bitcoin DCA Calculator →
          </Link>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-5">
            Smart DCA vs regular DCA FAQ
          </h2>

          <div className="space-y-4">
            {faqItems.map((item) => (
              <div
                key={item.q}
                className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5"
              >
                <h3 className="font-semibold text-white mb-2">
                  {item.q}
                </h3>

                <p className="text-zinc-400 leading-7 m-0">
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">
            Bottom line
          </h2>

          <p className="text-zinc-400 leading-7 mb-4">
            In this historical three-month comparison, Smart Timing
            accumulated more Bitcoin than buying at the close of each
            bi-weekly window with the same planned budget.
          </p>

          <p className="text-zinc-400 leading-7">
            The result is useful as a case study of execution timing, but it
            should not be treated as a promise about future performance.
            Different Bitcoin price paths can produce different outcomes.
          </p>
        </section>

        <section className="rounded-2xl border border-orange-500/20 bg-orange-500/5 p-6 mb-10">
          <p className="text-xs uppercase tracking-[0.18em] text-orange-400 font-semibold mb-2">
            Test your own schedule
          </p>

          <h2 className="text-2xl font-bold text-white mt-0 mb-3">
            Compare regular DCA with Smart Timing
          </h2>

          <p className="text-zinc-400 leading-7 mb-5">
            Choose your normal recurring amount and cadence to see a
            historical comparison using SatStacker's calculator.
          </p>

          <div className="flex flex-wrap gap-3">
            <Link
              to="/calculator"
              className="inline-block rounded-xl bg-orange-500 px-5 py-3 font-bold text-white hover:bg-orange-600"
            >
              Bitcoin DCA Calculator →
            </Link>

            <Link
              to="/smart-dca"
              className="inline-block rounded-xl border border-zinc-700 px-5 py-3 font-semibold text-white hover:border-orange-500"
            >
              Smart DCA Guide
            </Link>
          </div>
        </section>

        <section className="border-t border-zinc-800 pt-8">
          <p className="font-semibold text-white mb-4">
            Continue learning
          </p>

          <div className="space-y-3">
            <Link
              to="/smart-dca"
              className="block text-orange-400 hover:underline"
            >
              Smart DCA Guide →
            </Link>

            <Link
              to="/bitcoin-dca"
              className="block text-orange-400 hover:underline"
            >
              How to DCA Bitcoin →
            </Link>

            <Link
              to="/calculator"
              className="block text-orange-400 hover:underline"
            >
              Bitcoin DCA Calculator →
            </Link>

            <Link
              to="/blog/best-way-to-buy-bitcoin"
              className="block text-orange-400 hover:underline"
            >
              Best Way to Buy Bitcoin →
            </Link>

            <Link
              to="/blog/best-bitcoin-apps-2026"
              className="block text-orange-400 hover:underline"
            >
              Best Bitcoin DCA Apps →
            </Link>
          </div>
        </section>
      </article>
    </main>
  );
}
